/**
 * Split-payment arithmetic for the payment screen. Pure and in integer paise.
 *
 * The cashier enters what each customer tender AMOUNT is: cash is what was handed over (it
 * may exceed what is due), UPI / card / other are the exact amount charged. From that this
 * module works out what the server will accept:
 *
 *   - non-cash amounts are sent as they are and must not, together, exceed the bill;
 *   - cash legs absorb whatever is left of the bill, in order, and the rest of the cash is
 *     change (sent as `tenderedPaise` on the cash payment, never as extra `amountPaise`);
 *   - the `amountPaise` values of the payload therefore sum EXACTLY to the grand total.
 *
 * Mirrors `settlePayments` in @sp/core (tendered/change semantics), which the tests check.
 */

import { tr } from '../i18n';

export type TenderMethod = 'CASH' | 'UPI' | 'CARD' | 'OTHER';

export interface TenderLeg {
  method: TenderMethod;
  /** Cash: handed over. Others: charged. */
  paise: number;
  reference: string;
}

export interface PaymentPayload {
  method: TenderMethod;
  amountPaise: number;
  reference: string | null;
  tenderedPaise?: number;
}

export interface SplitPlan {
  /** Payload that satisfies the server contract, or `null` while there are blocking issues. */
  payments: PaymentPayload[] | null;
  /** Still to collect. Zero when the bill is covered. */
  remainingPaise: number;
  changePaise: number;
  /** Total of everything the customer is handing over or paying. */
  tenderedPaise: number;
  /** Inline validation, one message per problem, with the leg it belongs to (-1 = whole bill). */
  issues: { index: number; field: 'amount' | 'reference' | 'bill'; message: string }[];
}

export function planSplit(totalPaise: number, legs: TenderLeg[]): SplitPlan {
  const issues: SplitPlan['issues'] = [];

  const nonCashPaise = legs.reduce((sum, leg) => (leg.method === 'CASH' ? sum : sum + leg.paise), 0);
  const cashHandedPaise = legs.reduce((sum, leg) => (leg.method === 'CASH' ? sum + leg.paise : sum), 0);
  const tenderedPaise = nonCashPaise + cashHandedPaise;

  const cashDuePaise = Math.max(0, totalPaise - nonCashPaise);
  const cashAppliedPaise = Math.min(cashHandedPaise, cashDuePaise);
  const remainingPaise = Math.max(0, totalPaise - nonCashPaise - cashHandedPaise);
  const changePaise = cashHandedPaise - cashAppliedPaise;

  legs.forEach((leg, index) => {
    if (leg.paise < 0 || !Number.isInteger(leg.paise)) {
      issues.push({ index, field: 'amount', message: tr('pay.issue.amount') });
    }
    if ((leg.method === 'UPI' || leg.method === 'CARD') && leg.paise > 0 && leg.reference.trim() === '') {
      issues.push({
        index,
        field: 'reference',
        message: leg.method === 'UPI' ? tr('pay.issue.upiRef') : tr('pay.issue.cardRef'),
      });
    }
  });

  if (nonCashPaise > totalPaise) {
    issues.push({
      index: -1,
      field: 'bill',
      message: tr('pay.issue.overExact'),
    });
  }
  if (legs.every((leg) => leg.paise === 0)) {
    issues.push({ index: -1, field: 'bill', message: tr('pay.issue.nothing') });
  } else if (remainingPaise > 0) {
    issues.push({ index: -1, field: 'bill', message: tr('pay.issue.short') });
  }

  if (issues.length > 0) return { payments: null, remainingPaise, changePaise, tenderedPaise, issues };

  let cashLeft = cashDuePaise;
  const payments: PaymentPayload[] = [];
  for (const leg of legs) {
    if (leg.paise <= 0) continue;
    if (leg.method === 'CASH') {
      const applied = Math.min(leg.paise, cashLeft);
      cashLeft -= applied;
      // A cash leg that ended up covering nothing (the other tenders paid it all) is not a payment.
      if (applied <= 0) continue;
      payments.push({ method: 'CASH', amountPaise: applied, reference: null, tenderedPaise: leg.paise });
    } else {
      payments.push({ method: leg.method, amountPaise: leg.paise, reference: leg.reference.trim() || null });
    }
  }

  return { payments, remainingPaise, changePaise, tenderedPaise, issues };
}

/** Parse "1,250.50" / "250" typed in rupees into integer paise. Empty or junk -> 0. No floats are kept. */
export function parseRupeesToPaise(text: string): number {
  const cleaned = text.replace(/[^\d.]/g, '');
  if (cleaned === '' || cleaned === '.') return 0;
  const [whole = '0', fraction = ''] = cleaned.split('.');
  const paise = Number.parseInt((fraction + '00').slice(0, 2), 10);
  return Number.parseInt(whole || '0', 10) * 100 + (Number.isFinite(paise) ? paise : 0);
}

/** The amount shown in an editable money box: `1250 -> "12.50"`. */
export function paiseToInput(paise: number): string {
  const whole = Math.trunc(paise / 100);
  const frac = String(Math.abs(paise % 100)).padStart(2, '0');
  return `${whole}.${frac}`;
}
